import React, { useState, useEffect } from 'react';
import { Plus, Trash, TrendUp, TrendDown } from '@phosphor-icons/react';
import axios from '../../utils/axiosInstance';

const API = `${window.location.origin}/api`;

export default function UroTab() {
  const [paliwoRecords, setPaliwoRecords] = useState([]);
  const [naprawyRecords, setNaprawyRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Form states for Paliwo (His debts)
  const [showPaliwoForm, setShowPaliwoForm] = useState(false);
  const [paliwoData, setPaliwoData] = useState('');
  const [paliwoKwota, setPaliwoKwota] = useState('');
  
  // Form states for Naprawy (My debts)
  const [showNaprawyForm, setShowNaprawyForm] = useState(false);
  const [naprawyData, setNaprawyData] = useState('');
  const [naprawyKwota, setNaprawyKwota] = useState('');
  const [naprawyPojazd, setNaprawyPojazd] = useState('');
  
  useEffect(() => {
    fetchRecords();
  }, []);
  
  const fetchRecords = async () => {
    setLoading(true);
    try {
      const [paliwoRes, naprawyRes] = await Promise.all([
        axios.get(`${API}/uro/paliwo`),
        axios.get(`${API}/uro/naprawy`)
      ]);
      setPaliwoRecords(paliwoRes.data);
      setNaprawyRecords(naprawyRes.data);
    } catch (err) {
      console.error('Error fetching URO records:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const addPaliwo = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/uro/paliwo`, {
        data: paliwoData,
        kwota: parseFloat(paliwoKwota)
      });
      setPaliwoData('');
      setPaliwoKwota('');
      setShowPaliwoForm(false);
      fetchRecords();
    } catch (err) {
      console.error('Error adding paliwo:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Błąd podczas dodawania wpisu';
      alert(`Błąd: ${errorMsg}`);
    }
  };
  
  const addNaprawy = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/uro/naprawy`, {
        data: naprawyData,
        kwota: parseFloat(naprawyKwota),
        pojazd: naprawyPojazd
      });
      setNaprawyData('');
      setNaprawyKwota('');
      setNaprawyPojazd('');
      setShowNaprawyForm(false);
      fetchRecords();
    } catch (err) {
      console.error('Error adding naprawy:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Błąd podczas dodawania wpisu';
      alert(`Błąd: ${errorMsg}`);
    }
  };
  
  const deletePaliwo = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć ten wpis?')) return;
    try {
      await axios.delete(`${API}/uro/paliwo/${id}`);
      fetchRecords();
    } catch (err) {
      console.error('Error deleting paliwo:', err);
    }
  };
  
  const deleteNaprawy = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć ten wpis?')) return;
    try {
      await axios.delete(`${API}/uro/naprawy/${id}`);
      fetchRecords();
    } catch (err) {
      console.error('Error deleting naprawy:', err);
    }
  };
  
  // Calculate totals
  const paliwoTotal = paliwoRecords.reduce((sum, r) => sum + (r.kwota || 0), 0);
  const naprawyTotal = naprawyRecords.reduce((sum, r) => sum + (r.kwota || 0), 0);
  const saldo = paliwoTotal - naprawyTotal;
  
  if (loading) {
    return <div className="text-center py-12">Ładowanie...</div>;
  }
  
  return (
    <div className="max-w-[1920px] mx-auto px-6 py-6 space-y-8">
      <h2 className="text-3xl font-bold text-slate-800 mb-6">URO - Wzajemne rozliczenia</h2>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Paliwo - His debts to me */}
        <div className="bg-white rounded-sm shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-bold text-slate-800">Jego długi (Paliwo)</h3>
            <button
              onClick={() => setShowPaliwoForm(!showPaliwoForm)}
              className="px-4 py-2 bg-foreground hover:bg-black text-white rounded-lg font-semibold flex items-center gap-2 transition-colors"
            >
              <Plus className="w-5 h-5" weight="bold" />
              Dodaj wpis
            </button>
          </div>
          
          {showPaliwoForm && (
            <form onSubmit={addPaliwo} className="mb-6 p-4 bg-muted rounded-xl">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Data</label>
                  <input
                    type="date"
                    value={paliwoData}
                    onChange={(e) => setPaliwoData(e.target.value)}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Kwota (zł)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={paliwoKwota}
                    onChange={(e) => setPaliwoKwota(e.target.value)}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button type="submit" className="px-4 py-2 bg-foreground hover:bg-black text-white rounded-lg font-semibold">
                  Zapisz
                </button>
                <button
                  type="button"
                  onClick={() => setShowPaliwoForm(false)}
                  className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-semibold"
                >
                  Anuluj
                </button>
              </div>
            </form>
          )}
          
          <div className="space-y-3">
            {paliwoRecords.length === 0 ? (
              <p className="text-center py-8 text-slate-400">Brak wpisów</p>
            ) : (
              paliwoRecords.map((record) => (
                <div key={record.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                  <div>
                    <div className="text-sm text-slate-500">{record.data}</div>
                    <div className="text-xl font-bold text-slate-800">{record.kwota?.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł</div>
                  </div>
                  <button
                    onClick={() => deletePaliwo(record.id)}
                    className="p-2 text-red-500 hover:bg-red-100 rounded-lg transition-colors"
                  >
                    <Trash className="w-5 h-5" />
                  </button>
                </div>
              ))
            )}
          </div>
          
          <div className="mt-6 pt-6 border-t-2 border-emerald-300">
            <div className="flex justify-between items-center">
              <span className="text-lg font-bold text-slate-700">Suma:</span>
              <span className="text-3xl font-bold text-emerald-700">
                {paliwoTotal.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł
              </span>
            </div>
          </div>
        </div>
        
        {/* Naprawy - My debts to him */}
        <div className="bg-white rounded-sm shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-bold text-slate-800">Moje długi (Naprawy)</h3>
            <button
              onClick={() => setShowNaprawyForm(!showNaprawyForm)}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold flex items-center gap-2 transition-colors"
            >
              <Plus className="w-5 h-5" weight="bold" />
              Dodaj wpis
            </button>
          </div>
          
          {showNaprawyForm && (
            <form onSubmit={addNaprawy} className="mb-6 p-4 bg-red-50 rounded-xl">
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Data</label>
                  <input
                    type="date"
                    value={naprawyData}
                    onChange={(e) => setNaprawyData(e.target.value)}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Kwota (zł)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={naprawyKwota}
                    onChange={(e) => setNaprawyKwota(e.target.value)}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Pojazd</label>
                  <input
                    type="text"
                    value={naprawyPojazd}
                    onChange={(e) => setNaprawyPojazd(e.target.value)}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button type="submit" className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold">
                  Zapisz
                </button>
                <button
                  type="button"
                  onClick={() => setShowNaprawyForm(false)}
                  className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-semibold"
                >
                  Anuluj
                </button>
              </div>
            </form>
          )}
          
          <div className="space-y-3">
            {naprawyRecords.length === 0 ? (
              <p className="text-center py-8 text-slate-400">Brak wpisów</p>
            ) : (
              naprawyRecords.map((record) => (
                <div key={record.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                  <div>
                    <div className="text-sm text-slate-500">{record.data} • {record.pojazd}</div>
                    <div className="text-xl font-bold text-slate-800">{record.kwota?.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł</div>
                  </div>
                  <button
                    onClick={() => deleteNaprawy(record.id)}
                    className="p-2 text-red-500 hover:bg-red-100 rounded-lg transition-colors"
                  >
                    <Trash className="w-5 h-5" />
                  </button>
                </div>
              ))
            )}
          </div>
          
          <div className="mt-6 pt-6 border-t-2 border-red-300">
            <div className="flex justify-between items-center">
              <span className="text-lg font-bold text-slate-700">Suma:</span>
              <span className="text-3xl font-bold text-emerald-700">
                {naprawyTotal.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł
              </span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Final Balance */}
      <div className={`p-8 rounded-sm border border-border ${saldo >= 0 ? 'bg-muted' : 'bg-signal'}`}>
        <div className="flex items-center justify-between text-white">
          <div className="flex items-center gap-4">
            {saldo >= 0 ? (
              <TrendUp className="w-12 h-12" weight="bold" />
            ) : (
              <TrendDown className="w-12 h-12" weight="bold" />
            )}
            <div>
              <div className="text-xl font-semibold opacity-90">Saldo końcowe:</div>
              <div className="text-4xl font-bold">
                {saldo >= 0 ? 'Uro jest mi winien:' : 'Jestem winien Uro:'}
              </div>
            </div>
          </div>
          <div className="text-6xl font-bold">
            {Math.abs(saldo).toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł
          </div>
        </div>
      </div>
    </div>
  );
}
